/// A `Session` is a single play through a game — e.g. 20 flashcards
/// in a row, or one hangman round. Sessions hold transient state
/// only; persistent history lives in `attempts.ts`.

import type { Attempt } from "./attempts.js";

export interface SessionConfig {
  /// Module the session belongs to (e.g. "morse").
  module: string;
  /// Game within the module (e.g. "flashcards").
  game: string;
  /// Total cards / rounds the session will play.
  length: number;
}

export interface SessionResult {
  /// Total attempts logged (should equal `config.length` for finished sessions).
  total: number;
  /// First-try correct count.
  correct: number;
  /// Mean response time across all attempts.
  meanMs: number;
  /// All attempts in order; the caller can persist them via
  /// `recordAttempt` to feed into rolling hit-rate stats.
  attempts: readonly Attempt[];
}

/// In-memory accumulator. Games call `log()` after each card and
/// `summarise()` at the end. Not persisted — the caller chooses
/// what to write to localStorage (typically: every attempt via
/// recordAttempt + an optional final session record elsewhere).
export class SessionRecorder {
  private readonly entries: Attempt[] = [];

  constructor(public readonly config: SessionConfig) {}

  log(entry: Omit<Attempt, "module">): void {
    this.entries.push({ ...entry, module: this.config.module });
  }

  summarise(): SessionResult {
    const correct = this.entries.filter((e) => e.ok).length;
    const totalMs = this.entries.reduce((sum, e) => sum + e.ms, 0);
    return {
      total: this.entries.length,
      correct,
      meanMs: this.entries.length ? totalMs / this.entries.length : 0,
      attempts: this.entries,
    };
  }
}
